import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { cx } from '@/styled-system/css';
import { FavoriteButton, ShareButton } from 'app/shared/halo/components/features/listings/SocialActions';
import { ContactIcon, ExpandIcon, TotalMonthlyPriceBadgeIcon } from 'app/shared/halo/components/ui/icons';
import { MiniMap } from 'app/shared/halo/components/ui/MiniMap';
import FullscreenOverlay from 'app/shared/halo/components/ui/FullscreenOverlay';
import { useDetailPanel } from 'app/shared/halo/components/ui/DetailPanel';
import { NeighborhoodSpoke, NeighborhoodSpokeContent } from '../NeighborhoodModule/NeighborhoodSpoke';
import { ListingDataProvider, NeighborhoodProvider } from '../NeighborhoodModule/context';
import { buildResponsiveImageAttrs } from 'app/shared/utils/fedPhotos';
import { closeOverlay } from 'app/shared/halo/stores/immersiveOverlayStore';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import stringHelper from 'app/shared/utils/stringHelper';
import * as styles from './immersiveOverlay.styles';

type ImmersiveCardProps = {
  listing: Record<string, unknown>;
  animationState?: 'running' | 'idle';
};

export const ImmersiveCard = ({ listing, animationState = 'idle' }: ImmersiveCardProps) => {
  const dispatch = useDispatch();
  const { openPanel, isMobile } = useDetailPanel();
  const [imageLoaded, setImageLoaded] = useState(false);

  const photos = listing?.photos as Array<{ url?: string }> | undefined;
  const previewPhoto = listing?.previewPhoto;
  const listingPhotoUrl =
    photos?.[0]?.url || (typeof previewPhoto === 'string' ? previewPhoto : (previewPhoto as { url?: string })?.url);
  const hasPhoto = Boolean(listingPhotoUrl) && !(previewPhoto as { noPhoto?: boolean })?.noPhoto;
  const effectiveAnimationState = hasPhoto ? animationState : 'idle';
  const propertyName =
    (listing as unknown as { propertyName?: string })?.propertyName ||
    (listing as unknown as { displayName?: string })?.displayName ||
    (listing?.address as { street?: string })?.street;
  const address = listing?.address as { city?: string; state?: string } | undefined;
  const city = address?.city;
  const listingState = address?.state;
  const listingMinMaxPriceBeds = listing?.listingMinMaxPriceBeds as
    | {
        minPrice?: number;
        maxPrice?: number;
        bedDisplaySemiCompact?: string;
        bathDisplaySemiCompact?: string;
      }
    | undefined;
  const priceMin = listingMinMaxPriceBeds?.minPrice;
  const priceMax = listingMinMaxPriceBeds?.maxPrice;
  const unitCount = (listing as unknown as { modelTypeUnitCount?: number })?.modelTypeUnitCount;
  const geo = listing?.geo as { lat?: number; lon?: number } | undefined;
  const hasGeo = geo?.lat != null && geo?.lon != null;

  const handleOpenNeighborhood = () => {
    const NeighborhoodWithListing = ({ children }: { children: React.ReactNode }) => (
      <ListingDataProvider listing={listing}>
        <NeighborhoodProvider>{children}</NeighborhoodProvider>
      </ListingDataProvider>
    );
    openPanel('neighborhood', NeighborhoodSpokeContent, { provider: NeighborhoodWithListing });
  };

  const imageAttrs = listingPhotoUrl ? buildResponsiveImageAttrs(listingPhotoUrl, 1152, [768, 1152, 1536]) : null;

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const listingUri = (listing as unknown as { uriV2?: string })?.uriV2 || (listing as unknown as { uri?: string })?.uri;
  const listingPath = listingUri
    ? (dispatch(RouteActions.buildListingPath({ listingUri })) as unknown as string)
    : null;

  const handleViewDetails = (e: React.MouseEvent) => {
    // Allow middle-click / cmd-click to open in new tab natively
    if (e.metaKey || e.ctrlKey || e.button === 1) return;

    e.preventDefault();
    closeOverlay();
    if (listingPath) {
      window.router.transitionTo(listingPath);
    }
  };

  return (
    <div data-testid="immersive-card" className={styles.immersiveCard}>
      {hasGeo && !isMobile && (
        <button
          type="button"
          className={styles.immersiveMiniMapTrigger}
          onClick={handleOpenNeighborhood}
          aria-label="View neighborhood map"
        >
          <MiniMap lat={geo.lat as number} lon={geo.lon as number} size={54} />
        </button>
      )}
      {hasGeo && isMobile && (
        <FullscreenOverlay.Root defaultOpen={false}>
          <FullscreenOverlay.Trigger asChild>
            <button type="button" className={styles.immersiveMiniMapTrigger} aria-label="View neighborhood map">
              <MiniMap lat={geo.lat as number} lon={geo.lon as number} size={54} />
            </button>
          </FullscreenOverlay.Trigger>
          <FullscreenOverlay.Portal>
            <FullscreenOverlay.Backdrop>
              <FullscreenOverlay.Content>
                <ListingDataProvider listing={listing}>
                  <NeighborhoodSpoke />
                </ListingDataProvider>
              </FullscreenOverlay.Content>
            </FullscreenOverlay.Backdrop>
          </FullscreenOverlay.Portal>
        </FullscreenOverlay.Root>
      )}
      <div data-testid="immersive-actions" className={styles.immersiveRightActions}>
        <div className={styles.immersiveActionWrapper}>
          <FavoriteButton
            onClick={() => {
              /* Save clicked */
            }}
            aria-label="Save this listing"
          />
          <div className={styles.immersiveActionLabel}>Save</div>
        </div>
        <div className={styles.immersiveActionWrapper}>
          <button
            className={styles.immersiveActionButton}
            onClick={() => {
              /* Contact clicked */
            }}
            aria-label="Contact property"
          >
            <ContactIcon />
          </button>
          <div className={styles.immersiveActionLabel}>Contact</div>
        </div>
        <div className={cx(styles.immersiveActionWrapper, styles.immersiveSocialButtonOverride)}>
          <ShareButton
            onClick={() => {
              /* Share clicked */
            }}
            aria-label="Share this listing"
          />
          <div className={styles.immersiveActionLabel}>Share</div>
        </div>
      </div>

      <div className={styles.immersiveImageContainer}>
        {imageAttrs ? (
          <img
            src={imageAttrs.src}
            srcSet={imageAttrs.srcSet}
            sizes="100vw"
            alt="Property photo"
            className={cx(
              styles.immersiveImage,
              imageLoaded && styles.immersiveImageLoaded,
              imageLoaded && effectiveAnimationState === 'running' && styles.immersiveImagePanning,
            )}
            onLoad={handleImageLoad}
          />
        ) : (
          <img
            src={listingPhotoUrl}
            alt="Property photo"
            className={cx(
              styles.immersiveImage,
              imageLoaded && styles.immersiveImageLoaded,
              imageLoaded && effectiveAnimationState === 'running' && styles.immersiveImagePanning,
            )}
            onLoad={handleImageLoad}
          />
        )}
      </div>

      <h2 className={styles.immersivePropertyName}>{propertyName || 'Property Name'}</h2>

      <div className={styles.immersiveBottomOverlay}>
        {priceMin && priceMax && (
          <div className={styles.immersivePriceContainer}>
            <div className={styles.immersivePriceDisplay}>
              ${priceMin?.toLocaleString()} - ${priceMax?.toLocaleString()}/mo
            </div>
            <div className={styles.immersivePriceCheck}>
              <span className={styles.immersiveCheckIcon}>
                <TotalMonthlyPriceBadgeIcon />
              </span>
              <span>Total price</span>
            </div>
          </div>
        )}

        <div className={styles.immersiveDetailsText}>
          {listingMinMaxPriceBeds?.bedDisplaySemiCompact && (
            <span>
              {listingMinMaxPriceBeds.bedDisplaySemiCompact}
              {listingMinMaxPriceBeds.bathDisplaySemiCompact && (
                <span>, {listingMinMaxPriceBeds.bathDisplaySemiCompact}</span>
              )}
              {unitCount ? ` | ${stringHelper.pluralize(unitCount, 'unit', 'units')} available` : ''}
            </span>
          )}
        </div>

        <a
          className={styles.immersiveExpandButton}
          href={listingPath || '#'}
          onClick={handleViewDetails}
          aria-label="View full property details"
        >
          View full property details
          <span className={styles.immersiveExpandIcon}>
            <ExpandIcon />
          </span>
        </a>
      </div>
    </div>
  );
};
